/**
 * Reise-copilot: «lærer om deg»-minne. Copiloten samler varige preferanser brukeren
 * røper i samtalen (f.eks. «liker kystnære plasser», «unngår bomveier», «reiser med
 * hund») og mater dem inn i senere turplanlegging så rutene blir mer tilpasset.
 *
 *   users/{uid}/copilot_memory/main   — { enabled, facts[], updatedAt } (eier-only)
 *
 * Default PÅ. Kan tømmes og skrus av fra tannhjulet i chatten.
 */

import { doc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebaseService';

export interface CopilotMemory {
  /** Læring aktivert? Default true (også når dokumentet mangler). */
  enabled: boolean;
  /** Lærte, varige preferanser om brukeren (korte fraser). */
  facts: string[];
  updatedAt: number;
}

export const DEFAULT_COPILOT_MEMORY: CopilotMemory = { enabled: true, facts: [], updatedAt: 0 };

/** Maks antall lærte fakta vi beholder (nyeste vinner) — holder prompt-konteksten liten. */
export const MAX_COPILOT_FACTS = 30;

/** Normaliser en fakta-frase for dedup (trim, små bokstaver, kollaps mellomrom). Ren. */
function factKey(fact: string): string {
  return fact.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Slå nye fakta sammen med eksisterende — trim, dropp tomme, dedup (case-insensitivt),
 * cap til MAX_COPILOT_FACTS (beholder de nyeste). Ren funksjon, testbar.
 */
export function mergeFacts(existing: string[], incoming: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  // Nyeste sist i lista → itererer bakfra så nyeste vinner ved cap, men bevar rekkefølge.
  for (const f of [...existing, ...incoming]) {
    const trimmed = (f || '').trim();
    if (!trimmed) continue;
    const key = factKey(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  // Behold de nyeste MAX ved overflow (kutt fra starten = eldste).
  return out.length > MAX_COPILOT_FACTS ? out.slice(out.length - MAX_COPILOT_FACTS) : out;
}

function memoryRef(uid: string) {
  return doc(db, 'users', uid, 'copilot_memory', 'main');
}

/** Sanntids-abonnement på brukerens copilot-minne. Faller til default (enabled) hvis doc mangler. */
export function subscribeToCopilotMemory(uid: string, cb: (mem: CopilotMemory) => void): () => void {
  return onSnapshot(
    memoryRef(uid),
    (snap) => {
      if (!snap.exists()) { cb({ ...DEFAULT_COPILOT_MEMORY }); return; }
      const d = snap.data() as Partial<CopilotMemory>;
      cb({
        enabled: d.enabled !== false, // default true
        facts: Array.isArray(d.facts) ? d.facts : [],
        updatedAt: typeof d.updatedAt === 'number' ? d.updatedAt : 0,
      });
    },
    () => cb({ ...DEFAULT_COPILOT_MEMORY }),
  );
}

/**
 * Slå nye lærte fakta sammen inn i minnet. `existing` sendes inn (fra abonnementet) så
 * vi slipper en ekstra read. `now` injiseres for testbarhet. No-op hvis ingenting nytt.
 */
export async function addCopilotFacts(uid: string, existing: string[], incoming: string[], now: number): Promise<void> {
  const merged = mergeFacts(existing, incoming);
  // Ingen endring → ikke skriv.
  if (merged.length === existing.length && merged.every((f, i) => f === existing[i])) return;
  await setDoc(memoryRef(uid), { enabled: true, facts: merged, updatedAt: now }, { merge: true });
}

/** Skru læring av/på. `now` injiseres for testbarhet. */
export async function setCopilotLearningEnabled(uid: string, enabled: boolean, now: number): Promise<void> {
  await setDoc(memoryRef(uid), { enabled, updatedAt: now }, { merge: true });
}

/** Tøm alt copiloten har lært (beholder enabled-flagget). `now` injiseres for testbarhet. */
export async function clearCopilotMemory(uid: string, now: number): Promise<void> {
  await updateDoc(memoryRef(uid), { facts: [], updatedAt: now }).catch(async () => {
    // Doc finnes kanskje ikke ennå → setDoc.
    await setDoc(memoryRef(uid), { enabled: true, facts: [], updatedAt: now }, { merge: true });
  });
}

/**
 * Etter lokal «tøm minne» (clearedAt): ignorer stale Firestore-snapshots som fortsatt
 * bærer facts med updatedAt ≤ clearedAt (klassisk race: optimistic clear → gammel
 * onSnapshot overskriver). Godta tomme facts eller nyere writes (løfter barrieren).
 */
export function applyMemorySnapshotAfterClear(
  incoming: CopilotMemory,
  clearedAt: number,
): { memory: CopilotMemory; clearedAt: number } {
  if (clearedAt <= 0) return { memory: incoming, clearedAt: 0 };
  if (incoming.facts.length > 0 && incoming.updatedAt <= clearedAt) {
    return {
      memory: { ...incoming, facts: [], updatedAt: Math.max(incoming.updatedAt, clearedAt) },
      clearedAt,
    };
  }
  // Tom liste eller nyere skriv → barrieren kan løftes.
  return { memory: incoming, clearedAt: 0 };
}
